import React, { useCallback, useEffect } from 'react';
import { IconButton, Stack, Typography } from '@mui/material';
import styles from './SdkApplicationModal.module.scss';
import { Add, DeleteOutline } from '@mui/icons-material';
import { ModalComponent } from 'new/routes/RouterManager';
import { ModalOutProps } from 'new/routes/ModalDefault';
import { TextField } from 'new/elements/TextField';
import { Button } from 'new/elements/Button';
import { useFieldArray, useForm } from 'react-hook-form';
import { UseFormProps } from 'react-hook-form/dist/types';
import { useModals } from 'new/hooks/useModals';
import { SdkApplication } from '@nexla/@types';
import {
  CreateSdkApplicationPayload,
  UpdateSdkApplicationPayload,
} from 'js/actions/sdkApplications';

type Props = ModalOutProps & {
  mode: 'create' | 'edit';
  app?: SdkApplication;
  createApp?(data: CreateSdkApplicationPayload): Promise<void>;
  updateApp?(id: number, data: UpdateSdkApplicationPayload): Promise<void>;
};

export const SdkApplicationModal: ModalComponent = (props: Props) => {
  const modal = useModals();
  const { setModalActions, mode } = props;

  const isEdit = mode === 'edit';
  const app = isEdit ? props.app : null;

  const useFormInitialProps: UseFormProps<any> = {
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      name: app?.name ?? '',
      description: app?.description ?? '',
      allowed_origins: (app?.allowed_origins ?? []).map((origin) => ({ value: origin })),
    },
  };

  const {
    watch,
    register,
    control,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm(useFormInitialProps);

  const { fields, append, remove } = useFieldArray({ name: 'allowed_origins', control });

  const name = watch('name');

  const onSubmit = useCallback(async () => {
    try {
      const values = getValues();
      const allowed_origins = values.allowed_origins
        .map((entry: { value: string }) => entry.value)
        .filter(Boolean);

      if (isEdit) {
        await props.updateApp?.(app.id, {
          name: values.name,
          description: values.description,
          allowed_origins,
        });
      } else {
        await props.createApp?.({
          name: values.name,
          description: values.description,
          allowed_origins,
        });
      }
      modal.closeModal(SdkApplicationModal.modalConfig.name);
    } catch (e) {
      console.error('SdkApplicationModal.onSubmit', e.message);
    }
  }, [props, app, getValues, modal, isEdit]);

  useEffect(() => {
    setModalActions({
      ok: {
        text: isEdit ? 'Save' : 'Register',
        onClick: onSubmit,
        disabled: Object.keys(errors || {}).length > 0 || name === '',
      },
    });
  }, [name, errors]);

  return (
    <Stack flexDirection="column" gap="16px" minWidth="400px">
      <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
        <TextField
          label="Name"
          error={!!errors.name}
          helperText={errors.name?.message as string}
          {...register('name', { required: 'Name is required' })}
        />

        <TextField label="Description" {...register('description')} />

        <Stack flexDirection="column" gap="8px">
          <Typography variant="body2" fontWeight={500}>
            Allowed Origins
          </Typography>
          {fields.map((field, index) => (
            <Stack key={field.id} flexDirection="row" gap="8px" alignItems="center">
              <TextField
                fullWidth
                placeholder="https://app.example.com"
                {...register(`allowed_origins.${index}.value`)}
              />
              <IconButton
                size="small"
                onClick={() => remove(index)}
                data-testid={`delete-origin-${index}`}
              >
                <DeleteOutline fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <Button
            variant="text"
            size="small"
            startIcon={<Add />}
            onClick={() => append({ value: '' })}
            className={styles.addOriginButton}
          >
            Add Origin
          </Button>
        </Stack>
      </form>
    </Stack>
  );
};

SdkApplicationModal.modalConfig = {
  name: 'SdkApplicationModal',
  maxWidth: 'sm',
  title: 'SDK Application',
};
